import React from 'react';

import { Typography } from '@mui/material';

import { AusType } from 'constants/loanApplication';
import { StatusEnum } from 'constants/status';
import { formatNumberToCurrencyString } from 'utils/number';

import { textStyles } from './AusComparison.styles';
import FindingReport from './FindingReport';
import StatItem from './StatItem';

export const columns = [
  {
    width: '34%',
    content: 'Results',
    key: 'Results',
  },
  {
    width: '33%',
    content: (
      <img alt="fannieMae" height={36} src="/assets/images/common/fannieMae.png" width={128} />
    ),
    key: 'FannieMae',
  },
  {
    width: '33%',
    content: (
      <img alt="fannieMae" height={32} src="/assets/images/common/freddieMac.png" width={96} />
    ),
    key: 'FreddieMac',
  },
];

const notEligibleText = 'Not Eligible';

export const getRows = ({ ausSummary = {}, loanId }) => {
  const { duSummary = {}, lpSummary = {} } = ausSummary;

  return [
    {
      key: 'risk-eligibility',
      entries: [
        {
          key: 'row-name',
          content: (
            <Typography className="title" sx={textStyles} variant="regular14">
              AUS Risk Class/Eligibility
            </Typography>
          ),
        },
        {
          key: 'fannie-mae',
          content: (
            <StatItem
              status={duSummary.approveEligibility ? StatusEnum.SUCCESS : StatusEnum.ERROR}
              text={duSummary.approveEligibility ? 'Approve/Eligible' : notEligibleText}
            />
          ),
        },
        {
          key: 'freddie-mac',
          content: (
            <StatItem
              status={lpSummary.approveEligibility ? StatusEnum.SUCCESS : StatusEnum.ERROR}
              text={lpSummary.approveEligibility ? 'Accept/Eligible' : notEligibleText}
            />
          ),
        },
      ],
    },
    {
      key: 'waiver-eligibility',
      entries: [
        {
          key: 'row-name',
          content: (
            <Typography className="title" sx={textStyles} variant="regular14">
              Appraisal Waiver Eligibility
            </Typography>
          ),
        },
        {
          key: 'fannie-mae',
          content: (
            <StatItem
              status={duSummary.waiverEligibility ? StatusEnum.SUCCESS : StatusEnum.ERROR}
              text={
                duSummary.waiverEligibility
                  ? 'Value Acceptance (Appraisal Waiver)'
                  : notEligibleText
              }
            />
          ),
        },
        {
          key: 'freddie-mac',
          content: (
            <StatItem
              status={lpSummary.waiverEligibility ? StatusEnum.SUCCESS : StatusEnum.ERROR}
              text={lpSummary.waiverEligibility ? 'ACE' : notEligibleText}
            />
          ),
        },
      ],
    },
    {
      key: 'reserves-required',
      entries: [
        {
          key: 'row-name',
          content: (
            <Typography className="title" sx={textStyles} variant="regular14">
              Reserves Required
            </Typography>
          ),
        },
        {
          key: 'fannie-mae',
          content:
            duSummary.reservesRequired > 0 ? (
              <StatItem
                status={StatusEnum.WARNING}
                text={formatNumberToCurrencyString(duSummary.reservesRequired)}
              />
            ) : (
              <Typography sx={textStyles} variant="regular14">
                -
              </Typography>
            ),
        },
        {
          key: 'freddie-mac',
          content:
            lpSummary.reservesRequired > 0 ? (
              <StatItem
                status={StatusEnum.WARNING}
                text={formatNumberToCurrencyString(lpSummary.reservesRequired)}
              />
            ) : (
              <Typography sx={textStyles} variant="regular14">
                -
              </Typography>
            ),
        },
      ],
    },
    {
      key: 'collateral-rep',
      entries: [
        {
          key: 'row-name',
          content: (
            <Typography className="title" sx={textStyles} variant="regular14">
              Collateral Rep & Warrant Relief
            </Typography>
          ),
        },
        {
          key: 'fannie-mae',
          content: (
            <StatItem
              status={duSummary.collateralRepAndWarrantyRel ? StatusEnum.SUCCESS : StatusEnum.ERROR}
              text={duSummary.collateralRepAndWarrantyRel ? 'Eligible' : notEligibleText}
            />
          ),
        },
        {
          key: 'freddie-mac',
          content: (
            <StatItem
              status={lpSummary.collateralRepAndWarrantyRel ? StatusEnum.SUCCESS : StatusEnum.ERROR}
              text={lpSummary.collateralRepAndWarrantyRel ? 'Eligible' : notEligibleText}
            />
          ),
        },
      ],
    },
    {
      key: 'finding-report',
      entries: [
        {
          key: 'row-name',
          content: (
            <Typography className="title" sx={textStyles} variant="regular14">
              Findings Report
            </Typography>
          ),
        },
        {
          key: 'fannie-mae',
          content: duSummary.lastDocumentDate ? (
            <FindingReport
              lastDocumentDate={duSummary.lastDocumentDate}
              loanId={loanId}
              typeOfAus={AusType.DU}
            />
          ) : null,
          customStyles: {
            padding: '8px 15px',
          },
        },
        {
          key: 'freddie-mac',
          content: lpSummary.lastDocumentDate ? (
            <FindingReport
              lastDocumentDate={lpSummary.lastDocumentDate}
              loanId={loanId}
              typeOfAus={AusType.LP}
            />
          ) : null,
          customStyles: {
            padding: '8px 15px',
          },
        },
      ],
    },
  ];
};

export const ausTypeOptions = [
  {
    value: AusType.DU,
    label: 'Select DO',
  },
  {
    value: AusType.LP,
    label: 'Select LPA',
  },
];
